import { useCallback, useEffect, useState } from 'react';
import { Percent, IceCreamCone, Bike, BadgeIndianRupee, Wallet, Send, ArrowUpRight, ArrowDownLeft, Plus, Star, Clock } from 'lucide-react';
import { useCurrency, formatNumber } from '@kss/currency';
import { useToast, cn } from '@kss/ui';
import { useAuth } from '@kss/auth';
import { screenService, walletService, type LoyaltyInfo, type RewardTile, type WalletTxn, type ApiWalletTopup } from '@kss/api';
import { QUICK_ADD, type RewardIcon } from './rewardsData';

const REWARD_ICON: Record<RewardIcon, typeof Percent> = {
  percent: Percent,
  dessert: IceCreamCone,
  delivery: Bike,
  rupee: BadgeIndianRupee
};

type Tab = 'rewards' | 'wallet';

export default function RewardsWallet() {
  const { user } = useAuth();
  const phone = user?.phone ?? '';
  const [tab, setTab] = useState<Tab>('rewards');
  // Points, tier and the reward catalogue are REAL customer balances — never seeded. null
  // until the API answers, so a customer with no points never sees an invented "Gold · 1,240".
  const [loyalty, setLoyalty] = useState<LoyaltyInfo | null>(null);
  const [rewards, setRewards] = useState<RewardTile[]>([]);
  // The wallet is REAL money — never seed a balance. It shows the customer's live DB balance
  // (0 until a staff-confirmed top-up credits it), never a fabricated starter amount.
  const [wallet, setWallet] = useState<{ balance: number; txns: WalletTxn[] }>({ balance: 0, txns: [] });
  const [topups, setTopups] = useState<ApiWalletTopup[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const reload = useCallback(() => {
    screenService
      .getRewardsWallet(phone)
      .then((s) => {
        setLoyalty(s.loyalty ?? null);
        setRewards(s.rewards ?? []);
        setWallet(s.wallet ?? { balance: 0, txns: [] });
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
    if (phone) walletService.myTopups(phone).then(setTopups).catch(() => setTopups([]));
  }, [phone]);

  useEffect(() => reload(), [reload]);

  return (
    <div className='mx-auto w-full max-w-[560px] p-4 pb-24'>
      <div className='mb-4 flex items-center justify-between'>
        <h1 className='font-display text-xl font-bold'>Rewards &amp; Wallet</h1>
        <div className='flex gap-1 rounded-xl bg-surface-card-hover p-1'>
          <TabBtn active={tab === 'rewards'} onClick={() => setTab('rewards')} icon={<Star size={14} />}>
            Rewards
          </TabBtn>
          <TabBtn active={tab === 'wallet'} onClick={() => setTab('wallet')} icon={<Wallet size={14} />}>
            Wallet
          </TabBtn>
        </div>
      </div>

      {tab === 'rewards' ? (
        <RewardsTab loyalty={loyalty} rewards={rewards} loading={loading} failed={failed} />
      ) : (
        <WalletTab wallet={wallet} phone={phone} topups={topups} onRequest={reload} />
      )}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  icon,
  children
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-[12.5px] font-semibold transition',
        active ? 'bg-surface-card text-content-primary shadow-card' : 'text-content-muted'
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/* ─────────────────────────────  REWARDS  ───────────────────────────── */

function RewardsTab({
  loyalty,
  rewards,
  loading,
  failed
}: {
  loyalty: LoyaltyInfo | null;
  rewards: RewardTile[];
  loading: boolean;
  failed: boolean;
}) {
  const toast = useToast();
  const [points, setPoints] = useState(0);
  // No local "redeemed" set any more — redemption is server-side only, so the browser has
  // nothing to remember. See redeem() below.

  // Reset points when live loyalty arrives.
  useEffect(() => setPoints(loyalty?.points ?? 0), [loyalty?.points]);

  // Nothing from the server yet → say so. A points balance is money owed to the customer,
  // so it must never be filled in with a plausible-looking number.
  if (!loyalty) {
    return (
      <div className='rounded-2xl border border-dashed border-border p-8 text-center'>
        <Star size={26} className='mx-auto text-content-muted' />
        <div className='mt-2.5 font-display text-[14px] font-bold'>
          {loading ? 'Loading your points…' : failed ? 'Rewards unavailable' : 'No rewards account yet'}
        </div>
        <p className='mt-1 text-[12px] leading-relaxed text-content-muted'>
          {loading
            ? 'Fetching your balance and tier.'
            : failed
              ? 'We couldn’t reach the server. Pull down to retry in a moment.'
              : 'Place an order to start collecting points.'}
        </p>
      </div>
    );
  }

  const toNext = Math.max(0, loyalty.nextTierAt - points);
  const pct = loyalty.nextTierAt > 0 ? Math.min(100, Math.round((points / loyalty.nextTierAt) * 100)) : 0;

  /**
   * Redemption needs `POST /api/wallet/redeem`, which does not exist — `walletService`
   * only has topup / mine / pending / approve / reject.
   *
   * This used to deduct the points locally and show "Redeemed ✓". Nothing was issued and
   * nothing was recorded, so the customer walked to the counter holding a reward the
   * restaurant had no record of, with their points visibly gone. Points are a liability;
   * they must not be spent by the browser alone.
   */
  function redeem(r: RewardTile) {
    if (points < r.cost) {
      toast.show(`Need ${formatNumber(r.cost - points)} more points`, 'error');
      return;
    }
    toast.show(`Redeeming ${r.title} isn’t available yet — please ask at the counter`, 'info');
  }

  return (
    <>
      {/* Points card */}
      <div className='relative overflow-hidden rounded-2xl bg-[image:var(--gradient-primary)] p-5 text-white shadow-cta'>
        <div className='absolute -right-7 -top-7 h-32 w-32 rounded-full bg-white/10' />
        <div className='relative flex items-center justify-between'>
          <span className='text-xs font-semibold opacity-90'>YOUR POINTS</span>
          <span className='flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[10.5px] font-bold'>
            <Star size={11} className='fill-white' /> {loyalty.tier} tier
          </span>
        </div>
        <div className='relative mt-1.5 font-display text-[38px] font-extrabold leading-none'>
          {formatNumber(points)}
        </div>
        <div className='relative mt-3.5'>
          <div className='h-[7px] overflow-hidden rounded bg-white/25'>
            <div className='h-full rounded bg-white transition-all' style={{ width: `${pct}%` }} />
          </div>
          <div className='mt-2 text-[11px] opacity-90'>
            {toNext > 0 ? (
              <>
                {formatNumber(toNext)} pts to <b>{loyalty.nextTier}</b> · {loyalty.perk}
              </>
            ) : (
              <>
                <b>{loyalty.nextTier}</b> unlocked · {loyalty.perk}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Redeem grid */}
      <div className='mb-3 mt-5 font-display text-[15px] font-bold'>Redeem rewards</div>
      {rewards.length === 0 && (
        <div className='rounded-2xl border border-dashed border-border p-6 text-center text-[12.5px] text-content-muted'>
          No rewards are on offer right now.
        </div>
      )}
      <div className='grid grid-cols-2 gap-3'>
        {rewards.map((r) => {
          const Icon = REWARD_ICON[r.icon];
          
          const affordable = points >= r.cost;
          return (
            <div
              key={r.id}
              className={cn(
                'rounded-2xl border border-border bg-surface-card p-3.5 shadow-card transition',
                !affordable && 'opacity-60'
              )}
            >
              <span
                className='flex h-9 w-9 items-center justify-center rounded-xl'
                style={{ background: r.tint, color: r.color }}
              >
                <Icon size={17} />
              </span>
              <div className='mt-2.5 font-display text-[13px] font-bold'>{r.title}</div>
              <div className='text-[11px] text-content-muted'>{r.sub}</div>
              {/* No "Redeemed ✓" state: the browser cannot know that, because nothing is
                  redeemed until the server records it. */}
              <button
                onClick={() => redeem(r)}
                className={cn(
                  'mt-2.5 w-full rounded-lg py-1.5 font-display text-[11.5px] font-bold transition',
                  affordable ? 'bg-primary text-primary-foreground' : 'bg-surface-card-hover text-content-muted'
                )}
              >
                {`${formatNumber(r.cost)} pts`}
              </button>
            </div>
          );
        })}
      </div>

      {/* "Refer & earn 200 pts · For every friend who orders" used to sit here with an
          Invite button that only toasted "Invite link copied" — no clipboard write, no
          referral link, and no referral system anywhere in the product to honour it.
          Removed rather than left as a promise the restaurant cannot keep. */}
    </>
  );
}

/* ─────────────────────────────  WALLET  ───────────────────────────── */

function WalletTab({
  wallet,
  phone,
  topups,
  onRequest
}: {
  wallet: { balance: number; txns: WalletTxn[] };
  phone: string;
  topups: ApiWalletTopup[];
  onRequest: () => void;
}) {
  const { format } = useCurrency();
  const toast = useToast();
  const [amount, setAmount] = useState(QUICK_ADD[1]!);
  const [method, setMethod] = useState<'UPI' | 'Cash'>('UPI');
  const [busy, setBusy] = useState(false);
  // Balance + transactions are the REAL DB values from the parent — no local mutation.
  const { balance, txns } = wallet;
  const pending = topups.filter((t) => t.status === 'requested');

  // A top-up is a REQUEST: it credits nothing until staff confirm the cash at the counter.
  // The app has no payment gateway, so the client must never move money on its own.
  async function requestTopup() {
    if (!phone) {
      toast.show('Sign in to top up your wallet', 'error');
      return;
    }
    setBusy(true);
    try {
      await walletService.requestTopup(phone, amount, method);
      toast.show(`Top-up requested · ${format(amount)} — pay ${method} at the counter, staff will confirm`, 'success');
      onRequest();
    } catch {
      toast.show('Could not request the top-up, try again', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* Balance card */}
      <div className='relative overflow-hidden rounded-2xl bg-[#11151B] p-5 text-white'>
        <div className='absolute -bottom-8 -right-5 h-32 w-32 rounded-full bg-white/[.06]' />
        <div className='text-xs font-semibold opacity-70'>WALLET BALANCE</div>
        <div className='mt-1 font-display text-[34px] font-extrabold'>{format(balance)}</div>
        <div className='mt-4 flex gap-2.5'>
          <button
            onClick={requestTopup}
            disabled={busy}
            className='flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary py-3 font-display text-[12.5px] font-bold text-primary-foreground disabled:opacity-60'
          >
            <Plus size={15} /> Request top-up
          </button>
          <button
            onClick={() => toast.show('Send to a friend — coming soon', 'info')}
            className='flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-white/10 py-3 font-display text-[12.5px] font-bold text-white'
          >
            <Send size={14} /> Send
          </button>
        </div>
      </div>

      {/* Quick add */}
      <div className='mt-3.5 flex gap-2'>
        {QUICK_ADD.map((a) => (
          <button
            key={a}
            onClick={() => setAmount(a)}
            className={cn(
              'flex-1 rounded-xl border py-2.5 font-display text-[12.5px] font-bold transition',
              amount === a
                ? 'border-primary bg-primary-light text-primary'
                : 'border-border bg-surface-card text-content-secondary'
            )}
          >
            {format(a)}
          </button>
        ))}
      </div>

      {/* Pay method (recorded on the request; staff confirm the cash at the counter) */}
      <div className='mt-2 flex gap-2'>
        {(['UPI', 'Cash'] as const).map((m) => (
          <button
            key={m}
            onClick={() => setMethod(m)}
            className={cn(
              'flex-1 rounded-xl border py-2 font-display text-[12px] font-bold transition',
              method === m
                ? 'border-primary bg-primary-light text-primary'
                : 'border-border bg-surface-card text-content-secondary'
            )}
          >
            Pay {m}
          </button>
        ))}
      </div>

      {/* Pending top-up requests awaiting staff confirmation */}
      {pending.length > 0 && (
        <div className='mt-3 rounded-xl border border-warning/40 bg-warning-light/60 p-3'>
          <div className='mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-warning'>
            <Clock size={12} /> Awaiting counter confirmation
          </div>
          {pending.map((t) => (
            <div key={t.id} className='flex items-center justify-between py-0.5 text-[12.5px]'>
              <span className='font-semibold'>
                {format(t.amount)} · {t.method}
              </span>
              <span className='text-[11px] text-content-muted'>Requested {t.requestedAt} · pay at the counter</span>
            </div>
          ))}
        </div>
      )}

      {/* "Add ₹500, get ₹50 cashback" used to sit here. `requestTopup` applies no cashback
          and there is no cashback engine anywhere, so it was a money promise nothing
          honoured. Removed rather than shown to a paying customer. */}

      {/* Transactions */}
      <div className='mb-1 mt-5 font-display text-[14px] font-bold'>Transactions</div>
      {txns.length === 0 && <div className='py-3 text-[12.5px] text-content-muted'>No wallet activity yet.</div>}
      <div>
        {txns.map((t, i) => (
          <div
            key={t.id}
            className={cn('flex items-center gap-3 py-3', i < txns.length - 1 && 'border-b border-border')}
          >
            <span
              className={cn(
                'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg',
                t.kind === 'debit' ? 'bg-nonveg-light text-nonveg' : 'bg-veg-light text-veg'
              )}
            >
              {t.kind === 'debit' ? <ArrowUpRight size={16} /> : <ArrowDownLeft size={16} />}
            </span>
            <div className='min-w-0 flex-1'>
              <div className='text-[13px] font-semibold'>{t.label}</div>
              <div className='text-[11px] text-content-muted'>{t.sub}</div>
            </div>
            <span
              className={cn(
                'font-display text-[13.5px] font-bold',
                t.kind === 'debit' ? 'text-nonveg' : 'text-veg'
              )}
            >
              {t.kind === 'debit' ? '−' : '+'} {format(t.amount)}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}
